import * as Sentry from '@sentry/react';
import { alreadyReported } from '@services/lib/mutation-reporter';
import mixpanel from 'mixpanel-browser';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import useSWRMutation from 'swr/mutation';

import AuthorizationTooltip from '@components/AuthorizationTooltip';
import { sendAnalytics } from '@lib/sendAnalytics';
import { Button, Loader, Stack, StatusIcon, UItils } from '@nearst/ui';
import { useCheckPermission } from '@services/auth/hooks';
import { saveFileToS3 } from '@services/shop';
import { customiseProduct, useStock } from '@services/stock';
import { useIsChain } from '@utils/useIsChain';

import Image from '../Fields/Image';
import Text from '../Fields/Text';
import TextArea from '../Fields/TextArea';
import { FieldWithTag } from './FieldWithTag';
import { isEmpty } from './formHelpers';

import styles from './EditForm.module.scss';

async function optimisticallyUpdate(editProduct, data) {
	await editProduct(data, {
		optimisticData: (existingData) => ({ ...existingData, ...data }),
		rollbackOnError: true
	});
}

const EditForm = () => {
	const { shopId, barcode } = useParams();
	const { data: stockItem } = useStock(barcode, shopId, { suspense: true });
	const isChain = useIsChain();

	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const { trigger: editProduct } = useSWRMutation(`stock_data-${shopId}-${barcode}`, async (_, { arg: modification }) =>
		customiseProduct(barcode, shopId, modification)
	);
	const defaultValues = useMemo(
		() => ({
			barcode,
			title: stockItem?.title || '',
			brand: stockItem?.brand || '',
			description: stockItem?.description || '',
			link: stockItem?.link?.split('targetUrl=')[1]
				? decodeURIComponent(stockItem.link.split('targetUrl=')[1])
				: stockItem?.link || '',
			images: stockItem.images || [],
			image: undefined,
			imageSrc: stockItem?.images?.length ? stockItem.images[0] : undefined
		}),
		[barcode, stockItem]
	);
	const authorized = useCheckPermission('org:inventory:edit');

	const isFieldInconsistent = (fieldName) => {
		return isChain && stockItem?.inconsistentFields?.includes(fieldName);
	};
	const methods = useForm({
		defaultValues,
		// validate on change
		mode: 'all'
	});
	const {
		formState: { errors, isValid, isSubmitting, isDirty, isSubmitSuccessful },
		clearErrors,
		handleSubmit,
		reset,
		getValues,
		setError,
		register
	} = methods;

	const onSubmit = async (data) => {
		setErrorMessage('');
		setLoading(true);
		if (!authorized) {
			setLoading(false);
			throw Error('Not authorised');
		}
		if (Object.entries(errors).length || isEmpty(data)) {
			console.error(errors);
			setLoading(false);
			throw Error('Errors in data submitted');
		}
		try {
			// only upload if new image input (in which case it will be a File and not undefined)
			if (data.image) {
				const images = stockItem?.images || [];
				const imageUrl = await saveFileToS3(shopId, data.image);
				images.unshift(imageUrl);
				data.images = images;
			}

			try {
				await optimisticallyUpdate(editProduct, data);
			} catch (e) {
				console.error(e);
				// retry once
				await optimisticallyUpdate(editProduct, data);
			}
			sendAnalytics('Custom Product Form', 'Submit edit', shopId);
			mixpanel.track('Custom product edited', { distinct_id: shopId, barcode, ...data });

			// Reset form meta state (e.g. validation etc) to reset UI
			reset(data, { keepValues: true });
		} catch (error) {
			let message = 'Error submitting product edit form.';
			setErrorMessage('Sorry, something went wrong updating this product. Please try again or contact support.');
			// Joi validation throws in a specific way
			if (error.response?.data?.error?.message === 'Validation failed.') {
				message = 'Error submitting product edit form (validation failed server-side).';
				setErrorMessage('Additional data required to update this product.');
				// manually set validation errors on the form
				error.response.data.error.details.forEach((err) => {
					setError(err.context.key, { type: 'required', message: 'Required' });
				});
			}
			console.error(message);
			if (!alreadyReported(error)) Sentry.captureException(error);
		} finally {
			setLoading(false);
		}
	};
	const onError = (errors) => {
		// this scenario only happens if there's a validation fail
		// no need to message - validation errors should pop up
		console.error(errors);
	};
	const handleSave = async (e) => {
		e.preventDefault();
		handleSubmit(onSubmit, onError)(e);
	};
	const handleCancel = (e) => {
		e.preventDefault();
		// wipe clean - reset back to defaults
		reset();
		setErrorMessage('');
		clearErrors();
	};

	const canSubmit = isDirty && isValid && !isSubmitting;
	const canCancel = isDirty && !isSubmitting;
	const isValidationError = (Object.entries(errors).length || isEmpty(getValues())) && !stockItem.isGtin;
	const isSuccessFul = !isSubmitting && isSubmitSuccessful && !errorMessage;

	const Message = () => {
		// placeholder so UI doesn't jump around when message appears
		let message = <p className={styles.message}> </p>;
		if (isValidationError) {
			message = (
				<p className={UItils.classnames(styles.message, styles.validation)}>
					<StatusIcon status="error" />
					Additional data required to update this product.
				</p>
			);
		} else if (errorMessage) {
			message = (
				<p className={UItils.classnames(styles.message, styles.validation)}>
					<StatusIcon status="error" /> {errorMessage}
				</p>
			);
		} else if (isSuccessFul && isSubmitSuccessful && !isDirty) {
			message = (
				<p className={styles.message}>
					<StatusIcon status="active" /> Your changes have been applied.
				</p>
			);
		}
		return message;
	};

	return (
		<>
			<FormProvider {...methods}>
				<form className={styles.form} onSubmit={handleSave}>
					<Stack>
						<div className={styles.container}>
							<Stack className={styles.leftStack} space="0.5rem">
								<FieldWithTag showTag={isFieldInconsistent('title')} fieldName="title" barcode={barcode}>
									<Text name="title" required disabled={!authorized} {...register('title')} />
								</FieldWithTag>
								<FieldWithTag showTag={isFieldInconsistent('brand')} fieldName="brand" barcode={barcode}>
									<Text name="brand" required disabled={!authorized} {...register('brand')} />
								</FieldWithTag>
								<FieldWithTag showTag={isFieldInconsistent('link')} fieldName="link" barcode={barcode}>
									<Text name="link" icon="language" disabled={!authorized} {...register('link')} />
								</FieldWithTag>
							</Stack>
							<FieldWithTag
								showTag={isFieldInconsistent('images')}
								fieldName="images"
								barcode={barcode}
								className={UItils.classnames(styles.rightStack)}
							>
								<Image className={styles.fullWidth} name="imageSrc" required disabled={!authorized} />
							</FieldWithTag>
							<FieldWithTag
								showTag={isFieldInconsistent('description')}
								fieldName="description"
								barcode={barcode}
								className={styles.fullWidth}
							>
								<TextArea
									name="description"
									required
									disabled={!authorized}
									className={styles.fullWidth}
									{...register('description')}
								/>
							</FieldWithTag>
						</div>
						<div className={styles.actionsContainer}>
							<AuthorizationTooltip authorized={authorized}>
								<Button disabled={!authorized || !canSubmit} type="submit" primary>
									Save
								</Button>
							</AuthorizationTooltip>
							<Button disabled={!authorized || !canCancel} onClick={handleCancel}>
								Cancel
							</Button>
							{loading && <Loader size={16} />}
							<Message />
						</div>
					</Stack>
				</form>
			</FormProvider>
		</>
	);
};

export default EditForm;
